import { Inject, Injectable } from '@angular/core';
import { CldrIntlService, DateFieldNameOptions, DateFormatOptions, DateFormatPart } from '@progress/kendo-angular-intl';
import { WIM_Locale_Container, WIM_LOCALE_ID } from 'app/app.module';
import { Serialization } from "../../http/utils/serialization";
import { LABEL_SUFFIX, TranslateService } from "../../translate";
import { DateService } from "../../utils/date.service";
import { getPredefinedFormats } from './date-formats';

// public functions on this class must be arrow functions since the
// "this" context is often lost when calling them from the kendo components
// (e.g. kendo datepicker) and we have to use maintain the this reference between function calls
// notice injection is done after app.session.module
@Injectable({ providedIn: 'root' })//{ providedIn: 'any' }
  export class KendoDatetimeLocaleService extends CldrIntlService {

	private predefinedFormats: any;

	private seperator=", "; //todo - localize?

	constructor(@Inject(WIM_LOCALE_ID) localeId: WIM_Locale_Container, private dateService: DateService, private translateService :TranslateService) {
		super(localeId.getLocale());
		this.predefinedFormats = getPredefinedFormats(localeId);
	}

	// we know the locale only after the user has logged in. In theory we should not have an instance of this service before the user logs in
	// and the constructor should receive the correct value since we do not use it on the login page, but we want to make sure it is set correctly. 
	// This is the official kendo way for changing the locale at runtime.
	public forceLocale(localeId: WIM_Locale_Container) {
		this.localeId = localeId.getLocale();			    
	}

	public dateFieldName = (options: DateFieldNameOptions, localeId?: string): string=>{
		if ((this.localeId === "he-IL" && !localeId) || (localeId === "he-IL")) {
			switch (options.type) {
				case "year":
					return "הנש";
				case "month":
					return "שדוח";
				case "day":
					return "םוי";
				case "hour":
					return "העש";
				case "minute":
					return "הקד";
				default:
					break;
			}
		}
		return super.dateFieldName(options, localeId);
	}

	private get defaultDateFormat() {
		return this.predefinedFormats['Full'];
	}

	private get defaultTimeFormat() {
		return this.predefinedFormats['24H'];
	}

	private get defaultDatetimeFormat() {
		return this.predefinedFormats['fullDateTime'];
	}

	// returns the format to be used in the final parsing/formatting after checking with predefined formats:
	// in case of predefined formatting (look at date-formats.ts) - returns the format accordingly
	// in case of undefined format - returns the default date format
	// in all cases where the format is not a string pattern returns the format as is (returns the input)
	// in case of a string pattern return the format options for that input pattern
	private findPredefinedFormat(format: String | DateFormatOptions | string[] | DateFormatOptions[]) {
		if (typeof format !== "string") {
			//requested non string format, returning as is
			return format;
		}

		const predefinedFormat = this.predefinedFormats[format];
		if (predefinedFormat) {
			//requested predefined format
			return predefinedFormat;
		} else {
			// requested unrecognized format string - use as pattern
			return format;
		}
	}

	// override Kendo formatDate function in order to enable predefined date and time formats
	public formatDate = (value: Date | string, format?: String | DateFormatOptions, localeId?: string) : string=>{
		const date = this.convertToDate(value);
		const finalFormat =  format ? this.findPredefinedFormat(format) : this.defaultDateFormat;
		return super.formatDate(date , finalFormat, localeId);
	}

	public parseDate = (value: string, format?: string | DateFormatOptions | string[] | DateFormatOptions[], localeId?: string): Date =>{
		const finalFormat =  format ? this.findPredefinedFormat(format) : this.defaultDateFormat;
		return super.parseDate(value, finalFormat, localeId);
	}

	public splitDateFormat = (format: string | DateFormatOptions, localeId?: string): DateFormatPart[] =>{
		const finalFormat = format ? this.findPredefinedFormat(format) : this.defaultDateFormat;
		return super.splitDateFormat(finalFormat, localeId);
	}


	date = (value: Date | string, format?: string) : string =>{
		return this.formatDate(value, format);
	}

	public friendlyDate = (value: Date | string, format?: string, includeDayName?:boolean): string =>{
		const date = this.convertToDate(value);
		let friendlyDay;
		if (this.dateService.isToday(date)) {
			friendlyDay = this.translateFriendlyDate("Today"+LABEL_SUFFIX);
		}

		if (this.dateService.isTomorrow(date)) {
			friendlyDay = this.translateFriendlyDate("Tomorrow"+LABEL_SUFFIX);
		}

		if (this.dateService.isYesterday(date)) {
			friendlyDay = this.translateFriendlyDate("Yesterday"+LABEL_SUFFIX);
		}

		if (friendlyDay){
			if (includeDayName){
				return this.formatDate(date, format) + this.seperator +this.formatDate(date,"DayName")+" (" +friendlyDay+")";
			}else{
				return this.formatDate(date, format) + this.seperator +friendlyDay;
			}
		}else {
			if (includeDayName){
				return this.formatDate(date, format) +this.seperator +this.formatDate(date,"DayName");
			}else{
				return this.formatDate(date, format);
			}

		}
	}

	public time = (value: Date | string, format?: string) =>{
		const finalFormat = format ? this.findPredefinedFormat(format) : this.defaultTimeFormat;
		return this.formatDate(value, finalFormat);
	}

	public datetime = (value: Date | string, format?: string) =>{
		const finalFormat = format ? this.findPredefinedFormat(format) : this.defaultDatetimeFormat;
		return this.formatDate(value, finalFormat);
	}

	private translateFriendlyDate (value: string): string {
		return <string>this.translateService.instant(value);
	}

	private convertToDate (value: Date | string):Date{
		if (typeof value === 'string') {
			let retVal;
			let fullDateParsed =Serialization.convertDateStringsToDates(value);
			if (!fullDateParsed || !(fullDateParsed instanceof Date)){
				// the convertDateStringsToDates doesnt handle dates only (with no time).
				// we want to handle the the dates which were not convered here
				retVal=this.parseDate(value);
			}else{
				retVal=fullDateParsed;
			}
			return retVal;
		}
		return value;
	}
}
